import { useState } from 'react';
import { emitirFactura } from '../api/cfdi.api.js';
import { IcoClose, IcoCheck, IcoStore } from './common/Icons.jsx';
import styles from './FacturaModal.module.css';

const REGIMENES = [
  { value: '601', label: '601 — General de Ley Personas Morales' },
  { value: '612', label: '612 — Personas Físicas con Actividades Empresariales' },
  { value: '616', label: '616 — Sin obligaciones fiscales (XAXX)' },
  { value: '626', label: '626 — Régimen Simplificado de Confianza' },
];

const USO_CFDI = [
  { value: 'G01', label: 'G01 — Adquisición de mercancias' },
  { value: 'G03', label: 'G03 — Gastos en general' },
  { value: 'I01', label: 'I01 — Construcciones' },
  { value: 'S01', label: 'S01 — Sin efectos fiscales' },
];

export default function FacturaModal({ onClose, posPaymentId }) {
  const [form, setForm] = useState({
    rfc:            '',
    razon_social:   '',
    codigo_postal:  '',
    regimen_fiscal: '612',
    uso_cfdi:       'G03',
    email:          '',
  });
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [cfdi, setCfdi]         = useState(null);

  const onChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleEmitir = async () => {
    // Al público en general no se le emite factura individual: esa venta ya
    // entra en la factura global del periodo. Se dice aquí en vez de dejar que
    // el servidor lo rebote, porque el cajero lo va a intentar — hasta hace poco
    // era el primer botón de esta pantalla.
    if (form.rfc.toUpperCase().trim() === 'XAXX010101000') {
      setError('Sin RFC del cliente no se emite factura: esta venta entra en la factura global del periodo.');
      return;
    }
    // La factura se emite contra la venta. Sin ella el servidor rechaza, así
    // que se dice aquí en vez de mandar la petición al vacío.
    if (!posPaymentId) {
      setError('No se puede facturar: esta venta no quedó registrada en la caja.');
      return;
    }
    if (!form.rfc || !form.razon_social || !form.codigo_postal) {
      setError('RFC, razón social y código postal son obligatorios');
      return;
    }
    if (!form.email.trim()) {
      setError('El email es obligatorio para facturas con datos del cliente');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await emitirFactura({
        receptor: {
          rfc:            form.rfc.toUpperCase().trim(),
          razon_social:   form.razon_social.trim(),
          codigo_postal:  form.codigo_postal.trim(),
          regimen_fiscal: form.regimen_fiscal,
          uso_cfdi:       form.uso_cfdi,
          email:          form.email.trim() || undefined,
        },
        pos_payment_id: posPaymentId,
      });
      setCfdi(result);
    } catch (err) {
      const msg = err.response?.data?.error
        ?? err.response?.data?.data?.error_message
        ?? 'Error al emitir la factura';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        {cfdi ? (
          /* ── Estado éxito ──────────────────────────────────────────────── */
          <div className={styles.success}>
            <div className={styles.successIcon}><IcoCheck size={28} /></div>
            <h2 className={styles.successTitle}>¡Factura emitida!</h2>
            <p className={styles.successUuid}>UUID: {cfdi.uuid}</p>
            <p className={styles.successNote}>
              El folio es <strong>{cfdi.serie}{cfdi.folio}</strong>. La factura fue timbrada correctamente.
            </p>
            {/* La venta ya estaba declarada en la factura global: para no
                declararla dos veces, antes se emitió una nota de crédito que la
                saca de ahí. Salieron dos comprobantes y el negocio debe saberlo. */}
            {cfdi.notas_credito?.length > 0 && (
              <p className={styles.successNote}>
                Esta venta ya estaba en la factura global, así que antes se
                {cfdi.notas_credito.length === 1 ? ' emitió la nota de crédito ' : ' emitieron las notas de crédito '}
                <strong>{cfdi.notas_credito.map((n) => `${n.serie}${n.folio}`).join(', ')}</strong> para
                sacarla de ahí.
              </p>
            )}
            {form.email && (
              <p className={styles.successNote}>
                El XML fue enviado a <strong>{form.email}</strong>.
              </p>
            )}
            <button className="btn btn--primary btn--full" onClick={onClose}>
              Cerrar
            </button>
          </div>
        ) : (
          /* ── Formulario ──────────────────────────────────────────────────── */
          <>
            <div className={styles.header}>
              <h2 className={styles.title}>Solicitar factura</h2>
              <button className={styles.closeBtn} onClick={onClose} aria-label="Cerrar"><IcoClose size={13} /></button>
            </div>

            <div className={styles.body}>
              {/* Antes aquí había un atajo "Público en general" que timbraba a
                  XAXX. Ya no se emite así: sin RFC, la venta va en la factura
                  global. Se deja dicho para el cliente que no trae sus datos. */}
              <div className={styles.publicoBtn} style={{ cursor: 'default' }}>
                <span className={styles.publicoIcon}><IcoStore size={18} /></span>
                <div>
                  <div className={styles.publicoTitle}>¿El cliente no tiene RFC?</div>
                  <div className={styles.publicoSub}>
                    No hace falta facturar: su venta ya queda en la factura global del periodo.
                  </div>
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>RFC *</label>
                <input
                  className="input"
                  value={form.rfc}
                  onChange={(e) => onChange('rfc', e.target.value.toUpperCase())}
                  placeholder="XAXX010101000"
                  maxLength={13}
                  style={{ textTransform: 'uppercase' }}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Razón social *</label>
                <input
                  className="input"
                  value={form.razon_social}
                  onChange={(e) => onChange('razon_social', e.target.value)}
                  placeholder="NOMBRE O EMPRESA SA DE CV"
                />
              </div>

              <div className={styles.row}>
                <div className={styles.field}>
                  <label className={styles.label}>CP del receptor *</label>
                  <input
                    className="input"
                    value={form.codigo_postal}
                    onChange={(e) => onChange('codigo_postal', e.target.value)}
                    placeholder="06600"
                    maxLength={5}
                    inputMode="numeric"
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>Régimen fiscal</label>
                  <select
                    className="input"
                    value={form.regimen_fiscal}
                    onChange={(e) => onChange('regimen_fiscal', e.target.value)}
                  >
                    {REGIMENES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Uso CFDI</label>
                <select
                  className="input"
                  value={form.uso_cfdi}
                  onChange={(e) => onChange('uso_cfdi', e.target.value)}
                >
                  {USO_CFDI.map((u) => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  Email *
                </label>
                <input
                  className="input"
                  type="email"
                  value={form.email}
                  onChange={(e) => onChange('email', e.target.value)}
                  placeholder="cliente@ejemplo.com"
                  inputMode="email"
                />
              </div>

              {error && <p className={styles.error}>{error}</p>}
            </div>

            <div className={styles.footer}>
              <button className="btn btn--secondary" onClick={onClose} disabled={loading}>
                Cancelar
              </button>
              <button
                className="btn btn--primary"
                onClick={handleEmitir}
                disabled={loading}
                style={{ flex: 1 }}
              >
                {loading ? <span className="spinner" /> : 'Emitir factura'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
